import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { Fragment, useState } from 'react'
import { ChatBubbleOutlineRounded, SpeakerNotesOff } from '@mui/icons-material'
import { Avatar, Badge, Paper, Stack } from '@mui/material'
import { SPACING } from '@common/theme/spacing'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
}

export default function CommentModal({
  authorName,
  comment,
  requirementName,
}: {
  authorName?: string | null
  comment?: string[]
  requirementName: string
}) {
  const [open, setOpen] = useState(false)
  const handleClose = () => {
    setOpen(false)
  }

  const comments = comment?.filter((value) => value?.trim()) ?? []
  const hasComments = comments.length > 0
  const displayName = authorName?.trim() || null
  const initials = displayName
    ? displayName
        .split(' ')
        .filter(Boolean)
        .map((name) => name[0])
        .join('')
    : '?'

  return (
    <Fragment>
      <Stack
        direction={'row'}
        spacing={SPACING.buttonSpacing}
        alignItems={'center'}
        justifyContent={'space-between'}
        alignSelf={'stretch'}
        width={'100%'}
        onClick={() => setOpen(true)}
        data-testid="open-site-comment-modal"
      >
        <Badge color="error" badgeContent={comments.length} invisible={!hasComments}>
          <ChatBubbleOutlineRounded sx={{ color: 'text.secondary', width: 20, height: 20 }} />
        </Badge>
      </Stack>
      <Modal
        open={open}
        onClose={handleClose}
        data-testid="comment-modal"
        aria-labelledby="comment-modal-title"
        aria-describedby="comment-modal-description"
      >
        <Paper sx={modalStyle}>
          <Stack>
            <Stack padding={3} spacing={SPACING.commentModalSpacing}>
              <Stack height={32}>
                {hasComments && (
                  <Typography variant="h6" color="text.primary" id="comment-modal-title">
                    Site’s comment
                  </Typography>
                )}
              </Stack>
              <Stack height={24}>
                <Typography variant="body1" color="text.secondary" id="requirement-modal-title">
                  {requirementName}
                </Typography>
              </Stack>
            </Stack>
            <Stack paddingX={3} gap={1}>
              {hasComments ? (
                comments.map((value, index) => {
                  return (
                    <Box
                      key={`${index}-${value.slice(0, 24)}`}
                      sx={{
                        bgcolor: 'paper.main',
                        padding: 2,
                        borderRadius: 2,
                        width: 538,
                      }}
                    >
                      <Stack spacing={SPACING.commentBoxSpacing}>
                        <Stack direction={'row'} spacing={SPACING.minSpacing} alignItems={'center'}>
                          <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                            <Typography variant="caption" color="text.primary">
                              {initials}
                            </Typography>
                          </Avatar>
                          {displayName && (
                            <Typography variant="subtitle2" color="text.primary">
                              {displayName}
                            </Typography>
                          )}
                        </Stack>
                        <Stack alignSelf={'stretch'}>
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              overflow: 'hidden',
                              fontFeatureSettings: "'liga' off, 'clig' off",
                              textOverflow: 'ellipsis',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              display: '-webkit-box',
                            }}
                          >
                            {value}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  )
                })
              ) : (
                <Stack spacing={SPACING.minSpacing} width={538} alignItems={'center'}>
                  <Stack>
                    <SpeakerNotesOff
                      sx={{
                        width: 48,
                        height: 48,
                        color: 'text.disabled',
                      }}
                    />
                  </Stack>
                  <Stack alignItems={'center'}>
                    <Typography variant="h6" color="text.primary">
                      No comment added
                    </Typography>
                  </Stack>
                </Stack>
              )}
            </Stack>
          </Stack>
          <Stack padding={SPACING.commentModalButtonSpacing} alignSelf={'stretch'} alignItems={'flex-end'}>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="close-comment-task-button"
            >
              Close
            </Button>
          </Stack>
        </Paper>
      </Modal>
    </Fragment>
  )
}
